import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Lock } from "lucide-react";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "admin@bidoudou50.fr", password: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return <div className="p-10 text-center text-stone-500">Chargement…</div>;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      navigate("/admin");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-stone-100">
      <form
        data-testid="admin-login-form"
        onSubmit={onSubmit}
        className="bg-white border border-stone-200 rounded-sm p-8 w-full max-w-md space-y-5"
      >
        <div className="text-center">
          <div className="inline-flex w-12 h-12 items-center justify-center bg-orange-800 text-white rounded-sm">
            <Lock size={20} />
          </div>
          <h1 className="mt-4 font-serif text-3xl text-stone-900">Espace admin</h1>
          <p className="text-sm text-stone-500 mt-1">Bidoudou50 — Gestion des produits</p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="mt-1 w-full px-4 py-2.5 bg-stone-50 border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Mot de passe</label>
            <input
              data-testid="login-password-input"
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              className="mt-1 w-full px-4 py-2.5 bg-stone-50 border border-stone-300 rounded-sm focus:ring-2 focus:ring-orange-800 focus:outline-none"
            />
          </div>
        </div>

        {error && (
          <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm">
            {error}
          </div>
        )}

        <button
          data-testid="login-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full py-3 bg-orange-800 hover:bg-orange-900 disabled:bg-stone-400 text-white font-medium rounded-sm transition-colors"
        >
          {submitting ? "Connexion…" : "Se connecter"}
        </button>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="block w-full text-xs uppercase tracking-[0.15em] text-stone-500 hover:text-stone-900"
        >
          ← Retour à la boutique
        </button>
      </form>
    </div>
  );
}
